<template>
  <div class="alerts-panel-modern">
    <div class="panel-header">
      <h4 class="panel-title">
        <i class="fas fa-exclamation-triangle me-2"></i>
        Alertas Críticos
      </h4>
      <button class="btn-refresh-alerts" @click="$emit('refresh')">
        <i class="fas fa-sync-alt"></i>
      </button>
    </div>
    <div class="panel-body">
      <div v-if="alerts.length === 0" class="no-alerts-state">
        <div class="no-alerts-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h5>Tudo Funcionando!</h5>
        <p>Nenhum alerta crítico no momento</p>
      </div>
      
      <div v-else class="alerts-list">
        <div 
          v-for="(alert, index) in alerts.slice(0, 10)" 
          :key="alert.id || `alert-${index}-${alert.type}-${alert.server}-${Date.now()}`"
          class="alert-item-modern"
          :class="getAlertClass(alert.severity)"
        >
          <div class="alert-content">
            <div class="alert-main">
              <div class="alert-header">
                <div class="alert-icon" :class="getIconColorClass(alert.type)">
                  <i :class="getAlertIcon(alert.type)"></i>
                </div>
                <div class="alert-info">
                  <div class="server-name">{{ alert.server }}</div>
                  <div class="alert-message">{{ alert.message }}</div>
                </div>
              </div>
              
              <div class="alert-footer">
                <span class="alert-time">
                  <i class="fas fa-clock me-1"></i>
                  {{ formatDate(alert.timestamp) }}
                </span>
              </div>
            </div>
            
            <div class="alert-badges">
              <span 
                class="severity-badge" 
                :class="getSeverityBadgeClass(alert.severity)"
              >
                {{ getSeverityLabel(alert.severity) }}
              </span>
              <span v-if="alert.value" class="value-badge">
                {{ formatValue(alert.value, alert.type) }}
              </span>
            </div>
          </div>
        </div>
        
        <div v-if="alerts.length > 10" class="view-all-section">
          <router-link to="/server/alerts" class="btn-view-all">
            <i class="fas fa-list me-1"></i>
            Ver todos os {{ alerts.length }} alertas
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlertsPanel',
  props: {
    alerts: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    getAlertClass(severity) {
      switch (severity) {
        case 'critical':
          return 'border-danger bg-light-danger'
        case 'warning':
          return 'border-warning bg-light-warning'
        default:
          return 'border-info bg-light-info'
      }
    },
    
    getAlertIcon(type) {
      switch (type) {
        case 'disk':
          return 'fas fa-hdd'
        case 'ram':
          return 'fas fa-memory'
        case 'cpu':
          return 'fas fa-microchip'
        case 'application':
          return 'fas fa-code'
        default:
          return 'fas fa-exclamation-triangle'
      }
    },

    getIconColorClass(type) {
      switch (type) {
        case 'disk':
          return 'icon-warning'
        case 'ram':
          return 'icon-danger'
        case 'cpu':
          return 'icon-info'
        case 'application':
          return 'icon-primary'
        default:
          return 'icon-warning'
      }
    },
    
    getSeverityBadgeClass(severity) {
      switch (severity) {
        case 'critical':
          return 'badge-danger'
        case 'warning':
          return 'badge-warning'
        default:
          return 'badge-info'
      }
    },
    
    getSeverityLabel(severity) {
      switch (severity) {
        case 'critical':
          return 'Crítico'
        case 'warning':
          return 'Aviso'
        default:
          return 'Info'
      }
    },
    
    formatValue(value, type) {
      if (type === 'disk' || type === 'ram' || type === 'cpu') {
        return `${value}%`
      }
      if (type === 'application' && typeof value === 'number') {
        return `${value}MB`
      }
      return value
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      const now = new Date()
      const alertDate = new Date(date)
      const diffInMinutes = Math.floor((now - alertDate) / (1000 * 60))
      
      if (diffInMinutes < 1) {
        return 'Agora'
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}min atrás`
      } else if (diffInMinutes < 1440) {
        const hours = Math.floor(diffInMinutes / 60)
        return `${hours}h atrás`
      } else {
        return alertDate.toLocaleDateString('pt-BR')
      }
    }
  }
}
</script>

<style scoped>
/* Container Principal */
.alerts-panel-modern {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Header do Painel */
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.panel-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.btn-refresh-alerts {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
  color: white;
  padding: 6px 10px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh-alerts:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(102, 126, 234, 0.3);
}

/* Body do Painel */
.panel-body {
  flex: 1;
  padding: 20px;
  overflow: hidden;
}

/* Estado Sem Alertas */
.no-alerts-state {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.no-alerts-icon {
  font-size: 3rem;
  color: #28a745;
  margin-bottom: 15px;
}

.no-alerts-state h5 {
  color: #28a745;
  font-weight: 600;
  margin-bottom: 8px;
}

.no-alerts-state p {
  margin: 0;
  font-size: 0.9rem;
}

/* Lista de Alertas */
.alerts-list {
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Item de Alerta */
.alert-item-modern {
  border-radius: 10px;
  padding: 15px;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.alert-item-modern:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.alert-item-modern.border-danger {
  border-left-color: #dc3545;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.02) 100%);
}

.alert-item-modern.border-warning {
  border-left-color: #ffc107;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 193, 7, 0.02) 100%);
}

.alert-item-modern.border-info {
  border-left-color: #17a2b8;
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.05) 0%, rgba(23, 162, 184, 0.02) 100%);
}

/* Conteúdo do Alerta */
.alert-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
}

.alert-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.alert-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

/* Ícone do Alerta */
.alert-icon {
  width: 35px;
  height: 35px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: white;
  flex-shrink: 0;
}

.alert-icon.icon-danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
}

.alert-icon.icon-warning {
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
}

.alert-icon.icon-info {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
}

.alert-icon.icon-primary {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

/* Informações do Alerta */
.alert-info {
  flex: 1;
}

.server-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.alert-message {
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.4;
}

/* Footer do Alerta */
.alert-footer {
  margin-top: 8px;
}

.alert-time {
  font-size: 0.75rem;
  color: #adb5bd;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Badges do Alerta */
.alert-badges {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.severity-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.severity-badge.badge-danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
}

.severity-badge.badge-warning {
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
  color: #212529;
}

.severity-badge.badge-info {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;
}

.value-badge {
  background: #f8f9fa;
  color: #495057;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #dee2e6;
}

/* Seção Ver Todos */
.view-all-section {
  text-align: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #f1f3f4;
}

.btn-view-all {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.btn-view-all:hover {
  transform: translateY(-1px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
  color: white;
  text-decoration: none;
}

/* Scrollbar Personalizada */
.alerts-list::-webkit-scrollbar {
  width: 6px;
}

.alerts-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.alerts-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.alerts-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsividade */
@media (max-width: 768px) {
  .alert-content {
    flex-direction: column;
    gap: 10px;
  }
  
  .alert-badges {
    flex-direction: row;
    align-items: flex-start;
  }
  
  .panel-body {
    padding: 15px;
  }
}
</style> 